import React, { useState } from 'react';
import {
  Users,
  Image,
  Shield,
  MapPin,
  Database,
  Globe,
  Search,
  Network,
  Linkedin,
  Footprints,
  SquareLibrary,
  BookOpen,
  MessageCircle,
} from 'lucide-react';

// Human-readable titles for every page id, used to show "which page am I on" below the graph.
const PAGE_TITLES: Record<string, string> = {
  home: 'Home',
  contents: 'Contents',
  tools: 'Tools & Techniques',
  socmint: 'SOCMINT',
  'socmint-takeaways': 'SOCMINT — Key Takeaways',
  humint: 'HUMINT',
  imint: 'IMINT',
  cybint: 'CYBINT',
  geoint: 'GEOINT',
  finint: 'FININT',
  darkint: 'DARKINT',
  weapint: 'WEAPINT',
  walkthrough: 'OSINT Walkthrough',
  aggregation: 'Data Aggregation',
  draw: 'Evidence Board',
};

// Pages that sit outside the discipline graph but should still be one click away.
const QUICK_ACCESS = [
  { id: 'walkthrough', label: 'OSINT Walkthrough', icon: Footprints },
  { id: 'aggregation', label: 'Data Aggregation', icon: SquareLibrary },
  { id: 'contents', label: 'Contents', icon: BookOpen },
];

interface DisciplineNode {
  id: string;
  title: string;
  shortName: string;
  icon: React.ReactNode;
  color: string;
}

interface KnowledgeGraphSidebarProps {
  currentPage: string;
  completedSections: string[];
  onNavigate: (page: string) => void;
  onToggleComplete: (id: string) => void;
}

interface Position {
  x: number;
  y: number;
}

// ids match the App.tsx page ids (lowercase) so a node click navigates to a real page
// instead of a currentPage value nothing renders for.
const disciplines: DisciplineNode[] = [
  { id: 'humint', title: 'Human\nIntelligence', shortName: 'HUM', icon: <MessageCircle className="w-5 h-5" />, color: '#f97316' },
  { id: 'socmint', title: 'Social Media\nIntelligence', shortName: 'SOC', icon: <Users className="w-5 h-5" />, color: '#f97316' },
  { id: 'imint', title: 'Image\nIntelligence', shortName: 'IMI', icon: <Image className="w-5 h-5" />, color: '#f97316' },
  { id: 'cybint', title: 'Cyber\nIntelligence', shortName: 'CYB', icon: <Shield className="w-5 h-5" />, color: '#f97316' },
  { id: 'geoint', title: 'Geographic\nIntelligence', shortName: 'GEO', icon: <MapPin className="w-5 h-5" />, color: '#f97316' },
  { id: 'finint', title: 'Financial\nIntelligence', shortName: 'FIN', icon: <Database className="w-5 h-5" />, color: '#f97316' },
  { id: 'darkint', title: 'Darknet\nIntelligence', shortName: 'DAR', icon: <Globe className="w-5 h-5" />, color: '#f97316' },
  { id: 'weapint', title: 'Weapons\nIntelligence', shortName: 'WEA', icon: <Search className="w-5 h-5" />, color: '#f97316' },
];

// Define relationships between disciplines
const relationships = [
  { from: 'humint', to: 'socmint', label: 'sources' },
  { from: 'socmint', to: 'geoint', label: 'location' },
  { from: 'socmint', to: 'imint', label: 'faces/objects' },
  { from: 'imint', to: 'geoint', label: 'context' },
  { from: 'geoint', to: 'finint', label: 'infrastructure' },
  { from: 'cybint', to: 'finint', label: 'transactions' },
  { from: 'cybint', to: 'darkint', label: 'threats' },
  { from: 'darkint', to: 'weapint', label: 'trade' },
  { from: 'imint', to: 'weapint', label: 'specs' },
];

// Circular layout positions for 7 nodes
const getNodePosition = (index: number, total: number): Position => {
  const centerX = 160;
  const centerY = 200;
  const radius = 80;
  const angle = (index / total) * Math.PI * 2 - Math.PI / 2;
  return {
    x: centerX + radius * Math.cos(angle),
    y: centerY + radius * Math.sin(angle),
  };
};

const KnowledgeGraphSidebar: React.FC<KnowledgeGraphSidebarProps> = ({
  currentPage,
  completedSections,
  onNavigate,
  onToggleComplete,
}) => {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [hoveredEdge, setHoveredEdge] = useState<string | null>(null);

  const getNodeStatus = (id: string) => {
    if (completedSections.includes(id)) return 'completed';
    if (currentPage === id || currentPage === `${id}-takeaways`) return 'active';
    return 'pending';
  };

  const positions = disciplines.map((_, i) => getNodePosition(i, disciplines.length));

  return (
    <div className="fixed right-0 top-0 h-screen w-80 bg-slate-50 dark:bg-slate-800 border-l dark:border-slate-700 hidden xl:flex flex-col">
      <div className="p-6 pb-3 border-b dark:border-slate-700">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Network className="w-4 h-4 text-orange-500" />
            <h3 className="text-xs font-bold tracking-wider uppercase text-slate-900 dark:text-slate-100">
              Intelligence Map
            </h3>
          </div>
        </div>
        <p className="text-xs text-gray-600 dark:text-slate-400 mb-3">Click nodes to explore</p>

        {/* Legend — moved up here so it's visible without hovering or scrolling */}
        <div className="flex items-center gap-3 text-xs flex-wrap">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
            <span className="text-gray-700 dark:text-slate-300">Completed</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
            <span className="text-gray-700 dark:text-slate-300">In Progress</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-gray-300"></div>
            <span className="text-gray-700 dark:text-slate-300">Not Started</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="flex items-center justify-center p-4">
          <div className="relative">
          <svg width="320" height="450" viewBox="0 0 320 450" className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
            <defs>
              {/* Active node — orange→amber core */}
              <radialGradient id="nodeActive" cx="35%" cy="30%" r="75%">
                <stop offset="0%" stopColor="#fdba74" />
                <stop offset="55%" stopColor="#f97316" />
                <stop offset="100%" stopColor="#c2410c" />
              </radialGradient>
              {/* Completed node — emerald core */}
              <radialGradient id="nodeDone" cx="35%" cy="30%" r="75%">
                <stop offset="0%" stopColor="#6ee7b7" />
                <stop offset="55%" stopColor="#10b981" />
                <stop offset="100%" stopColor="#047857" />
              </radialGradient>
              {/* Backdrop glow behind the graph */}
              <radialGradient id="mapBg" cx="50%" cy="44%" r="60%">
                <stop offset="0%" stopColor="#f97316" stopOpacity="0.12" />
                <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
              </radialGradient>
              <filter id="nodeGlow" x="-70%" y="-70%" width="240%" height="240%">
                <feGaussianBlur stdDeviation="3.5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* backdrop glow + faint orbit ring the disciplines sit on */}
            <rect x="0" y="0" width="320" height="450" fill="url(#mapBg)" />
            <circle
              cx="160"
              cy="200"
              r="80"
              fill="none"
              stroke="var(--map-edge)"
              strokeWidth="1"
              strokeDasharray="2 6"
              opacity="0.7"
            />

            {/* Relationship lines */}
            {relationships.map((rel, idx) => {
              const fromIdx = disciplines.findIndex((d) => d.id === rel.from);
              const toIdx = disciplines.findIndex((d) => d.id === rel.to);
              const fromPos = positions[fromIdx];
              const toPos = positions[toIdx];
              const edgeKey = `${rel.from}-${rel.to}`;
              const isHovered = hoveredEdge === edgeKey;

              return (
                <g key={edgeKey}>
                  {/* invisible fat hit-area so thin lines are easy to hover */}
                  <line
                    x1={fromPos.x}
                    y1={fromPos.y}
                    x2={toPos.x}
                    y2={toPos.y}
                    stroke="transparent"
                    strokeWidth={12}
                    className="cursor-pointer"
                    onMouseEnter={() => setHoveredEdge(edgeKey)}
                    onMouseLeave={() => setHoveredEdge(null)}
                  />
                  {/* visible flowing dashed line */}
                  <line
                    x1={fromPos.x}
                    y1={fromPos.y}
                    x2={toPos.x}
                    y2={toPos.y}
                    stroke={isHovered ? '#f97316' : 'var(--map-edge)'}
                    strokeWidth={isHovered ? 2.5 : 1.5}
                    strokeDasharray="4 6"
                    strokeLinecap="round"
                    className="transition-all pointer-events-none"
                  >
                    <animate
                      attributeName="stroke-dashoffset"
                      from="0"
                      to="-20"
                      dur={isHovered ? '0.7s' : '3s'}
                      repeatCount="indefinite"
                    />
                  </line>
                  {isHovered && (
                    <text
                      x={(fromPos.x + toPos.x) / 2}
                      y={(fromPos.y + toPos.y) / 2 - 5}
                      fontSize="10"
                      fill="#f97316"
                      textAnchor="middle"
                      className="pointer-events-none font-bold"
                    >
                      {rel.label}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Draw nodes */}
            {disciplines.map((discipline, idx) => {
              const pos = positions[idx];
              const status = getNodeStatus(discipline.id);
              const isActive = status === 'active';
              const isCompleted = status === 'completed';
              const isHovered = hoveredNode === discipline.id;
              const fill = isActive
                ? 'url(#nodeActive)'
                : isCompleted
                ? 'url(#nodeDone)'
                : 'var(--map-pending)';
              const haloColor = isCompleted ? '#10b981' : '#f97316';
              const r = isHovered ? 27 : 24;

              return (
                <g
                  key={discipline.id}
                  onClick={() => onNavigate(discipline.id)}
                  onMouseEnter={() => setHoveredNode(discipline.id)}
                  onMouseLeave={() => setHoveredNode(null)}
                  className="cursor-pointer"
                >
                  {/* pulsing halo — active node breathes; hover shows a static glow */}
                  {(isActive || isHovered) && (
                    <circle cx={pos.x} cy={pos.y} r="26" fill={haloColor} opacity="0.25">
                      {isActive && (
                        <>
                          <animate
                            attributeName="r"
                            values="26;38;26"
                            dur="2.4s"
                            repeatCount="indefinite"
                          />
                          <animate
                            attributeName="opacity"
                            values="0.35;0.05;0.35"
                            dur="2.4s"
                            repeatCount="indefinite"
                          />
                        </>
                      )}
                    </circle>
                  )}

                  {/* main node */}
                  <circle
                    cx={pos.x}
                    cy={pos.y}
                    r={r}
                    fill={fill}
                    className="transition-all duration-200"
                    stroke="#ffffff"
                    strokeOpacity={isActive || isCompleted ? 0.7 : isHovered ? 0.5 : 0}
                    strokeWidth={1.5}
                    filter={isActive ? 'url(#nodeGlow)' : undefined}
                  />

                  {/* completion toggle — click to tick/untick without navigating */}
                  {(isCompleted || isHovered) && (
                    <g
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleComplete(discipline.id);
                      }}
                      className="cursor-pointer"
                    >
                      <circle
                        cx={pos.x + 17}
                        cy={pos.y - 17}
                        r="8"
                        fill={isCompleted ? '#10b981' : '#ffffff'}
                        stroke={isCompleted ? '#ffffff' : '#94a3b8'}
                        strokeWidth="1.5"
                      />
                      <text
                        x={pos.x + 17}
                        y={pos.y - 13}
                        fontSize="10"
                        fill={isCompleted ? '#ffffff' : '#94a3b8'}
                        textAnchor="middle"
                        className="font-bold pointer-events-none"
                      >
                        ✓
                      </text>
                    </g>
                  )}

                  {/* short-name label */}
                  <text
                    x={pos.x}
                    y={pos.y + 1}
                    fontSize={isHovered ? 11 : 10}
                    fill="white"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="font-bold pointer-events-none transition-all"
                  >
                    {discipline.shortName}
                  </text>
                </g>
              );
            })}
          </svg>

          {/* Floating tooltip anchored to the hovered node — no layout shift */}
          {hoveredNode && (() => {
            const idx = disciplines.findIndex((d) => d.id === hoveredNode);
            const pos = positions[idx];
            return (
              <div
                className="absolute z-10 pointer-events-none w-44 bg-blue-50 dark:bg-blue-500/10 rounded-lg border border-blue-200 dark:border-blue-500/30 px-3 py-2 shadow-lg"
                style={{ left: pos.x, top: pos.y - 34, transform: 'translate(-50%, -100%)' }}
              >
                <p className="text-sm font-bold text-blue-900 dark:text-blue-300 whitespace-pre-line">
                  {disciplines[idx].title}
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  Click to {getNodeStatus(hoveredNode) === 'completed' ? 'review' : 'start'} this section
                </p>
              </div>
            );
          })()}
          </div>
        </div>

        {/* Quick Access */}
        <div className="px-6 pb-4">
          <h4 className="text-xs font-bold tracking-wider uppercase text-slate-900 dark:text-slate-100 mb-2">
            Quick Access
          </h4>
          <div className="space-y-1">
            {QUICK_ACCESS.map((item) => {
              const Icon = item.icon;
              const active = currentPage === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onNavigate(item.id)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                    active
                      ? 'bg-orange-500 text-white font-semibold'
                      : 'text-gray-700 dark:text-slate-300 hover:bg-orange-50 dark:hover:bg-orange-500/10'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* On this page */}
        <div className="px-6 pt-2 pb-4">
          <h4 className="text-xs font-bold tracking-wider uppercase text-slate-900 dark:text-slate-100 mb-2">
            On this page
          </h4>
          <p className="text-sm font-semibold text-orange-600 dark:text-orange-400">
             {PAGE_TITLES[currentPage] || currentPage}
          </p>
        </div>
      </div>

      {/* LinkedIn footer */}
      <div className="p-4 border-t dark:border-slate-700">
        <a
          href="https://www.linkedin.com/in/joshua-holtz1"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-slate-300 hover:text-orange-500 dark:hover:text-orange-400 transition-colors"
        >
          <Linkedin className="w-4 h-4" />
          Connect on LinkedIn
        </a>
      </div>
    </div>
  );
};

export default KnowledgeGraphSidebar;
